import React, { useEffect, useState, useCallback } from "react";
import { Button, message, Input, Select, Row, Col } from "antd";
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import { getUserRole } from "../../utils/auth";

const { Option } = Select;

const StoreStockReport = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [stores, setStores] = useState([]);
  const [searchStores, setSearchStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [storeToDelete, setStoreToDelete] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 10, // Fixed pageSize to 10
    total: 0,
  });

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchQueryStore, setSearchQueryStore] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [allStockData, setAllStockData] = useState([]);
  const [excelLoading, setExcelLoading] = useState(false);

  const [filters, setFilters] = useState({
    storeId: "",
    products: "",
  });

  console.log("Filters:", filters);

  const getStocks = async () => {
    try {
      setLoading(true);
      const { current, pageSize } = paginationInfo;
      const response = await API.get("/stocks/stock-report", {
        params: {
          pageNumber: current,
          pageLimit: pageSize,
          search: searchQuery,
          storeId: filters.storeId,
          products: filters.products,
        },
      });

      const fetchedStock = response.data?.data.stocks || [];

      const transformedData = fetchedStock?.map((item) => ({
        _id: item._id,
        product: {
          _id: item.productId._id,
          specifications: {
            name: item.productId.specifications?.name || "N/A",
          },
          code: item.productId.barcode?.code || "N/A",
        },
        quantity: item.quantity || 0,
      }));

      setStores(transformedData);
      setPaginationInfo((prev) => ({
        ...prev,
        total: response.data?.data.pagination?.total || 0,
        pageSize: 10, // Enforce pageSize to 10
      }));
    } catch (err) {
      console.error("Error fetching stock data:", err);
      message.error("Failed to fetch stock data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getAllStockData = async () => {
    try {
      const response = await API.get("/stocks/stock-report", {
        params: {
          search: searchQuery,
          storeId: filters.storeId,
          products: filters.products,
        },
      });

      const fetchedStock = response.data.stock || [];
      const transformedData = fetchedStock.map((item) => ({
        _id: item._id,
        product: {
          _id: item.productId,
          specifications: {
            name: item.productName || "N/A",
          },
          code: item.productCode || "N/A",
        },
        quantity: item.quantity || 0,
      }));

      setAllStockData(transformedData);
    } catch (err) {
      console.error("Error fetching all stock data:", err);
    }
  };

  const fetchProducts = async (search) => {
    try {
      const params = { search };
      const res = await API.get("/products/paginate", { params });
      const fetchedProducts = res?.data?.products || [];

      const mergedProducts = [
        ...fetchedProducts,
        ...selectedProduct.filter(
          (selected) =>
            !fetchedProducts.some((fetched) => fetched._id === selected._id)
        ),
      ];

      setProducts(mergedProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
      message.error("Failed to fetch products.");
    }
  };

  const fetchStoreList = async (search) => {
    try {
      setLoading(true);
      const response = await API.get("/stores", {
        params: { search },
      });
      setSearchStores(response.data);

      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      const userStoreId = userData.storeId;
      const userRoleValue = getUserRole();

      if (userRoleValue.role !== "admin" && userStoreId) {
        const matchingStore = response.data.find(
          (store) => store._id === userStoreId
        );
        if (matchingStore) {
          setSelectedStoreId(matchingStore._id);
        }
      }
    } catch (err) {
      console.error("Error fetching stores:", err);
      message.error("Failed to fetch stores. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProduct && selectedProduct.length > 0) {
      // const productNames = selectedProduct
      //   .map((product) => product.specifications?.name)
      //   .filter(Boolean);
      // handleFilterChange("products", productNames);

      const productIds = selectedProduct.map((product) => product._id);
      handleFilterChange("products", productIds);
    } else {
      handleFilterChange("products", []);
    }
  }, [selectedProduct]);

  useEffect(() => {
    if (selectedStoreId) {
      handleFilterChange("storeId", selectedStoreId);
    } else {
      handleFilterChange("storeId", "");
    }
  }, [selectedStoreId]);

  useEffect(() => {
    fetchProducts("");
    fetchStoreList("");
  }, []);

  useEffect(() => {
    getStocks();
    getAllStockData();
  }, [paginationInfo.current, searchQuery, filters]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPaginationInfo((prev) => ({ ...prev, current: 1, pageSize: 10 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPaginationInfo((prev) => ({ ...prev, current: 1, pageSize: 10 }));
  };

  const handleDeleteStore = useCallback(async () => {
    try {
      await API.delete(`/stores/${storeToDelete._id}`);
      setStores((prev) =>
        prev.filter((store) => store._id !== storeToDelete._id)
      );
      setIsDeleteModalVisible(false);
      message.success("Store deleted successfully!");
    } catch (err) {
      console.error("Error deleting store:", err);
      message.error(err.response?.data?.message);
    }
  }, [storeToDelete]);

  const handleEditClick = (store) => {
    setIsEdit(true);
    setSelectedStore({
      ...store,
      managerId: store.managerId?._id || store.managerId,
    });
    setOpenDrawer(true);
  };

  const showDeleteModal = (store) => {
    setStoreToDelete(store);
    setIsDeleteModalVisible(true);
  };

  const downloadPDF = async () => {
    if (!stores || stores.length === 0) {
      message.warning("No data available to download");
      return;
    }

    setPdfLoading(true);
    try {
      if (allStockData.length === 0) {
        await getAllStockData();
      }

      const dataForPDF = allStockData.length > 0 ? allStockData : stores;

      let jsPDF;
      try {
        const jsPDFModule = await import("jspdf");
        jsPDF = jsPDFModule.default;
      } catch (importError) {
        console.error("Failed to import jsPDF:", importError);
        throw new Error("Failed to load PDF library");
      }

      try {
        await import("jspdf-autotable");
      } catch (importError) {
        console.error("Failed to import jspdf-autotable:", importError);
      }

      const doc = new jsPDF();

      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("Store Stock Report", 105, 20, { align: "center" });

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 105, 30, {
        align: "center",
      });

      if (selectedStoreId) {
        const selectedStore = searchStores.find(
          (store) => store._id === selectedStoreId
        );
        if (selectedStore) {
          doc.text(`Store: ${selectedStore.name}`, 105, 40, {
            align: "center",
          });
        }
      }

      if (typeof doc.autoTable === "function") {
        const tableData = dataForPDF.map((item, index) => [
          index + 1,
          item.product?.specifications?.name || "N/A",
          item.quantity || 0,
        ]);

        doc.autoTable({
          startY: 50,
          head: [["Product Index", "Products", "Quantity"]],
          body: tableData,
          theme: "grid",
          headStyles: {
            fillColor: [102, 126, 234],
            textColor: 255,
            fontStyle: "bold",
          },
          styles: {
            fontSize: 10,
            cellPadding: 5,
          },
          columnStyles: {
            0: { cellWidth: 30 },
            1: { cellWidth: 100 },
            2: { cellWidth: 30, halign: "center" },
          },
          didDrawPage: function (data) {
            doc.setFontSize(10);
            doc.text(
              `Page ${doc.internal.getNumberOfPages()}`,
              data.settings.margin.left,
              doc.internal.pageSize.height - 10
            );
          },
        });
      } else {
        let yPosition = 50;
        const lineHeight = 8;
        let pageNumber = 1;

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Product Index", 20, yPosition);
        doc.text("Products", 50, yPosition);
        doc.text("Quantity", 150, yPosition);

        yPosition += lineHeight;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        dataForPDF.forEach((item, index) => {
          if (yPosition > 250) {
            doc.setFontSize(10);
            doc.text(`Page ${pageNumber}`, 20, 280);

            doc.addPage();
            pageNumber++;
            yPosition = 20;

            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text("Product Index", 20, yPosition);
            doc.text("Products", 50, yPosition);
            doc.text("Quantity", 150, yPosition);
            yPosition += lineHeight;

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
          }

          const productIndex = index + 1;
          const productName = item.product?.specifications?.name || "N/A";
          const quantity = item.quantity || 0;

          doc.text(productIndex.toString(), 20, yPosition);
          doc.text(productName, 50, yPosition);
          doc.text(quantity.toString(), 150, yPosition);

          yPosition += lineHeight;
        });

        doc.setFontSize(10);
        doc.text(`Page ${pageNumber}`, 20, 280);
      }

      doc.addPage();

      const totalProducts = dataForPDF.length;
      const zeroStockProducts = dataForPDF.filter(
        (item) => (item.quantity || 0) <= 0
      ).length;
      const inStockProducts = totalProducts - zeroStockProducts;

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Summary Report", 105, 30, { align: "center" });

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Stock Summary:", 20, 60);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.text(`Total Products: ${totalProducts}`, 20, 80);
      doc.text(`In Stock: ${inStockProducts}`, 20, 100);
      doc.text(`Out of Stock: ${zeroStockProducts}`, 20, 120);

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Additional Statistics:", 20, 150);
      doc.setFont("helvetica", "normal");

      const lowStockProducts = dataForPDF.filter(
        (item) => (item.quantity || 0) > 0 && (item.quantity || 0) <= 5
      ).length;
      doc.text(`Low Stock (≤5 items): ${lowStockProducts}`, 20, 170);

      const totalStockValue = dataForPDF.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0
      );
      doc.text(`Total Stock Items: ${totalStockValue}`, 20, 190);

      const timestamp = new Date().toISOString().split("T")[0];
      const storeName = selectedStoreId
        ? searchStores.find((store) => store._id === selectedStoreId)?.name
        : "All Stores";
      const filename = `Stock_Report_${storeName}_${timestamp}.pdf`;

      doc.save(filename);
      message.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error(`Failed to generate PDF: ${error.message}`);
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!stores || stores.length === 0) {
      message.warning("No data available to download");
      return;
    }

    setExcelLoading(true);
    try {
      if (allStockData.length === 0) {
        await getAllStockData();
      }

      const dataForExcel = allStockData.length > 0 ? allStockData : stores;

      let XLSX;
      try {
        const xlsxModule = await import("xlsx");
        XLSX = xlsxModule.default || xlsxModule;
        if (!XLSX || !XLSX.utils) {
          throw new Error("XLSX library not properly loaded");
        }
      } catch (importError) {
        console.error("Failed to import XLSX:", importError);
        throw new Error("Failed to load Excel library");
      }

      const excelData = dataForExcel.map((item, index) => ({
        "Product Index": index + 1,
        "Product Name": item.product?.specifications?.name || "N/A",
        "Product Code": item.product?.code || "N/A",
        Quantity: item.quantity || 0,
        "Stock Status":
          (item.quantity || 0) <= 0
            ? "Out of Stock"
            : (item.quantity || 0) <= 5
            ? "Low Stock"
            : "In Stock",
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      const columnWidths = [
        { wch: 15 },
        { wch: 40 },
        { wch: 20 },
        { wch: 15 },
        { wch: 15 },
      ];
      worksheet["!cols"] = columnWidths;

      const totalProducts = dataForExcel.length;
      const zeroStockProducts = dataForExcel.filter(
        (item) => (item.quantity || 0) <= 0
      ).length;
      const inStockProducts = totalProducts - zeroStockProducts;
      const lowStockProducts = dataForExcel.filter(
        (item) => (item.quantity || 0) > 0 && (item.quantity || 0) <= 5
      ).length;
      const totalStockValue = dataForExcel.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0
      );

      const summaryData = [
        { Metric: "Total Products", Value: totalProducts },
        { Metric: "In Stock", Value: inStockProducts },
        { Metric: "Out of Stock", Value: zeroStockProducts },
        { Metric: "Low Stock (≤5 items)", Value: lowStockProducts },
        { Metric: "Total Stock Items", Value: totalStockValue },
      ];

      const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
      summaryWorksheet["!cols"] = [{ wch: 25 }, { wch: 15 }];

      XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Report");
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Summary");

      const timestamp = new Date().toISOString().split("T")[0];
      const storeName = selectedStoreId
        ? searchStores.find((store) => store._id === selectedStoreId)?.name
        : "All Stores";
      const filename = `Stock_Report_${storeName}_${timestamp}.xlsx`;

      XLSX.writeFile(workbook, filename);
      message.success("Excel file downloaded successfully!");
    } catch (error) {
      console.error("Error generating Excel:", error);
      message.error(`Failed to generate Excel: ${error.message}`);
    } finally {
      setExcelLoading(false);
    }
  };

  const columns = [
    {
      title: "Product Index",
      key: "productIndex",
      render: (_, __, index) =>
        (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Products",
      key: "products",
      dataIndex: ["product", "specifications", "name"],
    },
    {
      title: "Quantity",
      key: "quantity",
      dataIndex: "quantity",
      render: (quantity) => (
        <span
          style={{
            color: quantity <= 0 ? "red" : "black",
            fontWeight: "bold",
          }}
        >
          {quantity}
        </span>
      ),
    },
  ];

  return (
    <div
      style={{
        padding: "20px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Select
            placeholder="Select Store"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              const searchValue = input.toLowerCase();
              const text = option?.props?.children?.toLowerCase() || "";
              return text.includes(searchValue);
            }}
            onChange={(value) => setSelectedStoreId(value)}
            style={{ flex: 1, width: "100%" }}
            allowClear
            value={selectedStoreId}
          >
            {searchStores.length === 0 ? (
              <Option disabled>Select Stores</Option>
            ) : (
              searchStores.map((store) => (
                <Option key={store?._id} value={store?._id}>
                  {store?.name}
                </Option>
              ))
            )}
          </Select>
        </Col>
        <Col span={8}>
          <Select
            mode="multiple"
            placeholder="Select Products"
            value={selectedProduct?.map((prod) => prod._id)}
            onChange={(values) => {
              const selectedProducts = [
                ...selectedProduct.filter((prod) => values.includes(prod._id)),
                ...products.filter((prod) => values.includes(prod._id)),
              ];

              const uniqueSelectedProducts = Array.from(
                new Map(
                  selectedProducts.map((item) => [item._id, item])
                ).values()
              );

              setSelectedProduct(uniqueSelectedProducts);
            }}
            allowClear
            onSearch={(input) => fetchProducts(input)}
            style={{ flex: 1, width: "100%" }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.props?.optionText
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {products.map((product) => {
              const optionText = `${product.specifications.name || ""} (${
                product.code || "No Code"
              })`;

              return (
                <Option
                  key={product._id}
                  value={product._id}
                  optionText={optionText}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>{`${product.specifications.name || ""} (${
                      product.code || "No Code"
                    })`}</span>
                  </div>
                </Option>
              );
            })}
          </Select>
        </Col>
      </Row>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="primary"
          icon={<FileExcelOutlined />}
          onClick={downloadExcel}
          loading={excelLoading}
          disabled={excelLoading || !stores || stores.length === 0}
          style={{ marginRight: "8px" }}
        >
          {excelLoading ? "Generating Excel..." : "Download Excel"}
        </Button>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={downloadPDF}
          loading={pdfLoading}
          disabled={pdfLoading || !stores || stores.length === 0}
          style={{ marginRight: "8px" }}
        >
          {pdfLoading ? "Generating PDF..." : "Download PDF"}
        </Button>
      </div>

      <CustomTable
        columns={columns}
        dataSource={stores}
        onPaginationChange={(pagination) => {
          setPaginationInfo({
            current: pagination.current,
            pageSize: 10, // Enforce pageSize to 10
            total: pagination.total || paginationInfo.total,
          });
        }}
        loading={loading}
        total={paginationInfo.total}
        showSizeChanger={false} // Disable page size changer to fix pageSize at 10
        pageSizeOptions={["10"]} // Restrict to only 10
        onShowSizeChange={(current, size) => {
          if (size === "All") {
            getAllStockData();
            setPaginationInfo({
              current: 1,
              pageSize: allStockData.length || paginationInfo.total,
              total: allStockData.length || paginationInfo.total,
            });
          } else {
            setPaginationInfo({
              current: 1,
              pageSize: 10, // Enforce pageSize to 10
              total: paginationInfo.total,
            });
          }
        }}
      />

      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteStore}
        onCancel={() => setIsDeleteModalVisible(false)}
        title="Confirm Deletion"
        content="Are you sure you want to delete this store? This action cannot be undone."
      />
    </div>
  );
};

export default StoreStockReport;
